import React, { FC, useState, useRef, useEffect, ChangeEvent, FormEvent } from 'react'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { SearchResult, RequestErrorObject, SingleResponse, GlobalSearchHeaders } from '../../types'
import { searchService } from '../../services'
import styles from './index.module.less'
import Results from './Results'
import SearchBar from './SearchBar'
import RequestError from '../../services/RequestError'
import { handleError } from '../../components'
import { useAuth } from '../../providers/AuthProvider'

let debounceTimer: ReturnType<typeof setTimeout>

const GlobalSearch: FC<RouteComponentProps> = () => {
	const [searchResponse, setSearchResponse] = useState<SingleResponse<SearchResult[]>>()
	const [isLoading, setIsLoading] = useState<boolean>(null)
	const [value, setValue] = useState<string>('')
	const [resultsVisible, setResultsVisible] = useState<boolean>(false)
	const { userHasPermission } = useAuth()
	const node = useRef<HTMLDivElement>(null)
	const numberOfCharactersRequiredForSearch = 2
	const previousAbortController = useRef<AbortController>(null)

	const createSearchRequestError = (error: RequestError): RequestErrorObject => {
		const source = 'Search Error'
		const base = { source, status: error.response.status }

		switch (error.response.status) {
			case 422:
				return { ...base, description: 'Please enter at least two characters' }
			default:
				return { ...base, description: 'There was an unexpected problem when attempting to retrieve your results' }
		}
	}

	const resultClicked = (): void => {
		resetSearch()
		setResultsVisible(false)
	}

	const resetSearch = (): void => {
		setValue('')
		setSearchResponse(null)
		setResultsVisible(false)
	}

	const submitHandler = (e: FormEvent<EventTarget>): void => {
		e.preventDefault()
		if (value.length < numberOfCharactersRequiredForSearch) {
			const error = new RequestError('', { body: {}, status: 422 })
			handleError(error, createSearchRequestError)
		}
	}

	const search = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
		const { value } = e.target
		setValue(value)
		setResultsVisible(false)

		if (previousAbortController.current) {
			previousAbortController.current.abort()
		}

		if (!value.length) {
			resetSearch()
		} else if (value.length < numberOfCharactersRequiredForSearch) {
			setSearchResponse(null)
		} else {
			setResultsVisible(true)
			setIsLoading(true)
			clearTimeout(debounceTimer)
			debounceTimer = setTimeout(async () => {
				try {
					const abortController = new AbortController()
					previousAbortController.current = abortController

					const response = await searchService.search(value, abortController.signal)
					setSearchResponse(response)

					previousAbortController.current = null
					setIsLoading(false)
				} catch (e) {
					const error = e as Error
					if (error.name !== 'AbortError') {
						handleError(error, createSearchRequestError)
					} else {
						setIsLoading(false)
					}
				}
			}, 500)
		}
	}

	useEffect(() => {
		const handleKeydown = (e: KeyboardEvent): void => {
			if (e.ctrlKey === true && e.key === 'l') {
				e.preventDefault()
				node.current.focus()
			}
		}

		const handleClick = (e: MouseEvent): void => {
			if (node.current.contains(e.target as Node)) return
			setResultsVisible(false)
		}

		document.addEventListener('click', handleClick)
		window.addEventListener('keydown', handleKeydown)
		return (): void => {
			document.removeEventListener('click', handleClick)
			window.removeEventListener('keydown', handleKeydown)
		}
	}, [])

	const headers: GlobalSearchHeaders[] = ['end_customers', 'resellers']
	if (userHasPermission(['ServiceProvider.All.ReadWrite', 'ServiceProvider.Self.ReadWrite'])) {
		headers.push('service_providers')
	}

	const dataIncludesSubscribers = searchResponse?.data?.some(result => result.entity_type === 'consumer')
	if (dataIncludesSubscribers) {
		headers.push('subscribers')
	}

	return (
		<div className={styles.searchWrapper} ref={node}>
			<SearchBar searchTextChangeHandler={search} value={value} formSubmitHandler={submitHandler} clearHandler={resetSearch} />
			{resultsVisible && (
				<Results headers={headers} results={searchResponse ? searchResponse.data : []} loading={isLoading} resultClicked={resultClicked} />
			)}
		</div>
	)
}

export default withRouter(GlobalSearch)
